export const iosIntegrationBannerExample = (adUnitName: string) => `import PrebidMobile

let bidderConfig = BidderConfig(configId: "${adUnitName}")      
let bannerAdUnit = BannerAdUnit(configId: "${adUnitName}", size: CGSize(width: 320, height: 50))

bannerAdUnit.pbAdSlot = "${adUnitName}"
bannerAdUnit.gpid = "/\(GAMNetworkCode)/\(AdUnitPath)"
bannerAdUnit.add(bidder: bidderConfig)
`
export const iosIntegrationInterstitialExample = (adUnitName: string) => `import PrebidMobile

let bidderConfig = BidderConfig(configId: "${adUnitName}")   
let interstitialAdUnit = InterstitialAdUnit(configId: "${adUnitName}")

interstitialAdUnit.pbAdSlot = "${adUnitName}"
interstitialAdUnit.gpid = "/\(GAMNetworkCode)/\(AdUnitPath)" 
interstitialAdUnit.add(bidder: bidderConfig)
`
export const iosIntegrationNativeExample = (adUnitName: string) => `import PrebidMobile
import GoogleMobileAds

// 1. Build the native request
let request = NativeRequest(configId: "${adUnitName}")
request.context = .Content
request.placementType = .FeedContent

let title = NativeAssetTitle(length: 90, required: false)
let mainImage = NativeAssetImage(minimumWidth: 200, minimumHeight: 200, required: false)
mainImage.type = ImageAsset.Main
let icon = NativeAssetImage(minimumWidth: 50, minimumHeight: 50, required: false)
icon.type = ImageAsset.Icon
request.assets = [
    title, mainImage, icon,
    NativeAssetData(type: .sponsored, required: false),
    NativeAssetData(type: .description, required: false),
    NativeAssetData(type: .ctatext, required: false),
]
request.eventtrackers = [
    NativeEventTracker(event: .Impression, methods: [.Image, .js])
]

// 2. Fetch demand then load via GAM
let gamRequest = AdManagerRequest()
request.fetchDemand(adObject: gamRequest) { _ in
    let loader = AdLoader(adUnitID: "<gamAdUnitId>",
                          rootViewController: rootVC,
                          adTypes: [.customNative],
                          options: nil)
    loader.delegate = self          // CustomNativeAdLoaderDelegate
    loader.load(gamRequest)
}

// 3. CustomNativeAdLoaderDelegate
func customNativeAdFormatIDs(for adLoader: AdLoader) -> [String] { ["<customFormatId>"] }
func adLoader(_ adLoader: AdLoader, didReceive customNativeAd: CustomNativeAd) {
    Utils.shared.delegate = self    // NativeAdDelegate
    Utils.shared.findNative(adObject: customNativeAd)
}

// 4. NativeAdDelegate — render + register
func nativeAdLoaded(ad: PrebidMobile.NativeAd) {
    nativeAd.registerView(view: card, clickableViews: [ctaButton, mediaImageView])
    // ad.title / ad.text / ad.iconUrl / ad.imageUrl / ad.callToAction / ad.sponsoredBy
}
func nativeAdNotFound() {}
func nativeAdNotValid() {}
`

export const androidBannerIntegrationExample = (adUnitName: string) => `import org.prebid.mobile.BannerAdUnit
import org.prebid.mobile.AdSize
import org.prebid.mobile.BidderConfig

val bidderConfig = BidderConfig("${adUnitName}")
val bannerAdUnit = BannerAdUnit("${adUnitName}", AdSize(320, 50))

bannerAdUnit.pbAdSlot = "${adUnitName}"
bannerAdUnit.setGpid("/<NETWORK_CODE>/<AD_UNIT_PATH>") 
bannerAdUnit.addBidder(bidderConfig)`

export const androidInterstitialIntegrationExample = (adUnitName: string) => `import org.prebid.mobile.InterstitialAdUnit
import org.prebid.mobile.BidderConfig

val bidderConfig = BidderConfig("${adUnitName}")
val interstitialAdUnit = InterstitialAdUnit("${adUnitName}")

interstitialAdUnit.pbAdSlot = "${adUnitName}"
interstitialAdUnit.setGpid("/<NETWORK_CODE>/<AD_UNIT_PATH>")  
interstitialAdUnit.addBidder(bidderConfig)
`

export const androidIntegrationNativeExample = (adUnitName: string) => `import org.prebid.mobile.*
import com.google.android.gms.ads.*

// 1. Build the native ad unit
val adUnit = NativeAdUnit("${adUnitName}").apply {
    setContextType(NativeAdUnit.CONTEXT_TYPE.CONTENT_CENTRIC)
    setPlacementType(NativeAdUnit.PLACEMENTTYPE.CONTENT_FEED)
    addAsset(NativeTitleAsset().apply { setLength(90); setRequired(false) })
    addAsset(NativeImageAsset(200, 200, 200, 200).apply {
        setImageType(NativeImageAsset.IMAGE_TYPE.MAIN); setRequired(false)
    })
    addAsset(NativeImageAsset(50, 50, 50, 50).apply {
        setImageType(NativeImageAsset.IMAGE_TYPE.ICON); setRequired(false)
    })
    addAsset(NativeDataAsset().apply { setDataType(NativeDataAsset.DATA_TYPE.SPONSORED); setRequired(false) })
    addAsset(NativeDataAsset().apply { setDataType(NativeDataAsset.DATA_TYPE.DESC); setRequired(false) })
    addAsset(NativeDataAsset().apply { setDataType(NativeDataAsset.DATA_TYPE.CTATEXT); setRequired(false) })
    addEventTracker(NativeEventTracker(
        NativeEventTracker.EVENT_TYPE.IMPRESSION,
        arrayListOf(NativeEventTracker.EVENT_TRACKING_METHOD.IMAGE, NativeEventTracker.EVENT_TRACKING_METHOD.JS)
    ))
}

// 2. Fetch demand then load via GAM
val request = AdManagerAdRequest.Builder().build()
adUnit.fetchDemand(request) { _ ->
    AdLoader.Builder(context, "<gamAdUnitId>")
        .forCustomFormatAd(
            "<customFormatId>",
            { customAd -> AdViewUtils.findNative(customAd, prebidListener) },
            null
        )
        .withAdListener(object : AdListener() {
            override fun onAdFailedToLoad(error: LoadAdError) {}
        })
        .build()
        .loadAd(request)
}

// 3. PrebidNativeAdListener — render + register
override fun onPrebidNativeLoaded(ad: PrebidNativeAd) {
    ad.registerView(
        container,
        listOf(ctaButton, mediaImageView),
        object : PrebidNativeAdEventListener {
            override fun onAdImpression() {}
            override fun onAdClicked() {}
            override fun onAdExpired() {}
        }
    )
    // ad.title / ad.description / ad.iconUrl / ad.imageUrl / ad.callToAction / ad.sponsoredBy
}
override fun onPrebidNativeNotFound() {}
override fun onPrebidNativeNotValid() {}`

export const ampIntegrationExample = (adUnitName: string, siteId?: string) => {
  const textContent = `<amp-ad
  width="300"
  height="250"
  type="doubleclick"
  data-slot="/NETWORK_ID/ad-unit-path"
  rtc-config='{
    "urls": [
      "https://prebid-server.pbstck.com/openrtb2/amp?tag_id=${adUnitName}&w=ATTR(width)&h=ATTR(height)&slot=ATTR(data-slot)&curl=CANONICAL_URL&timeout=TIMEOUT&account_id=PUBSTACK_SITE_ID&gdpr_applies=CONSENT_METADATA(gdprApplies)&addtl_consent=CONSENT_METADATA(additionalConsent)&consent_type=CONSENT_METADATA(consentStringType)"
      ]
    }'>
  </amp-ad>`

  return siteId ? textContent.replace('PUBSTACK_SITE_ID', siteId) : textContent
}

export const androidInstreamIntegrationExample = (adUnitName: string) => `import org.prebid.mobile.*
import com.google.ads.interactivemedia.v3.api.*

val instreamAdUnit = InstreamVideoAdUnit(
  "${adUnitName}",
  AdSize(640, 480)
)

val videoParams = VideoParameters(arrayListOf("video/mp4"))
videoParams.protocols = arrayListOf(
  Signals.Protocols.VAST_2_0,
  Signals.Protocols.VAST_2_0_Wrapper,
  Signals.Protocols.VAST_3_0,
  Signals.Protocols.VAST_3_0_Wrapper,
  Signals.Protocols.VAST_4_0,
  Signals.Protocols.VAST_4_0_Wrapper)
// These fields must have these values
videoParams.plcmt = Signals.Plcmt.InStream
videoParams.placement = Signals.Placement.InStream
// The customer can customize this part
videoParams.playbackMethod = arrayListOf(Signals.PlaybackMethod.AutoPlaySoundOn)
videoParams.minDuration = 5
videoParams.maxDuration = 30
videoParams.minBitrate  = 300
videoParams.maxBitrate  = 2500
videoParams.api = arrayListOf(Signals.Api.OMID_1)
videoParams.isSkippable = true
videoParams.startDelay = Signals.StartDelay.Preroll

instreamAdUnit.videoParameters = videoParams
`
export const iosInstreamIntegrationExample = (adUnitName: string) => `import PrebidMobile
import GoogleInteractiveMediaAds

let instreamAdUnit = InstreamVideoAdUnit(
  configId: "${adUnitName}",
  size: CGSize(width: 640, height: 480)
)

let videoParams = VideoParameters(mimes: ["video/mp4"])
videoParams.protocols = [
  Signals.Protocols.VAST_2_0,
  Signals.Protocols.VAST_2_0_Wrapper,
  Signals.Protocols.VAST_3_0,
  Signals.Protocols.VAST_3_0_Wrapper,
  Signals.Protocols.VAST_4_0,
  Signals.Protocols.VAST_4_0_Wrapper
]
// These fields must have these values
videoParams.plcmt = Signals.Placement.InStream
videoParams.plcmt = Signals.Placement.InStream
// The customer can customize this part
videoParams.playbackMethod = [Signals.PlaybackMethod.AutoPlaySoundOn]
videoParams.minDuration = 5
videoParams.maxDuration = 30
videoParams.minBitrate  = 300
videoParams.maxBitrate  = 2500
videoParams.api = [Signals.Api.OMID_1]
videoParams.isSkippable = true
videoParams.startDelay = Signals.StartDelay.Preroll

instreamAdUnit.videoParameters = videoParams
`
